// Application shell: brand, primary navigation, language switcher, sign out.

import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Icon, type IconName } from './Icon';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { LOCALES, type Locale } from '@shared/types';
import type { TranslationKey } from '@shared/i18n/index';

const NAV: { to: string; label: TranslationKey; icon: IconName }[] = [
  { to: '/invoices', label: 'nav.invoices', icon: 'file-text' },
  { to: '/quotations', label: 'nav.quotations', icon: 'file-plus' },
  { to: '/purchases', label: 'nav.purchases', icon: 'inbox' },
  { to: '/clients', label: 'nav.clients', icon: 'users' },
  { to: '/products', label: 'nav.products', icon: 'box' },
  { to: '/reports', label: 'nav.reports', icon: 'clock' },
  { to: '/accounting', label: 'nav.accounting', icon: 'send' },
  { to: '/users', label: 'nav.users', icon: 'shield' },
  { to: '/settings', label: 'nav.settings', icon: 'settings' },
];

const LOCALE_LABELS: Record<Locale, string> = { es: 'ES', en: 'EN' };

export function Layout() {
  const t = useT();
  const { locale } = useI18n();
  const { company, user, signOut, setUserLocale } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // The menu is a mobile overlay; a navigation means it has served its purpose.
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="app">
      <header className="header">
        <NavLink to="/invoices" className="header__brand" onClick={closeMenu}>
          <img src="/favicon.svg" alt="" className="header__mark" />
          ARES Invoice
        </NavLink>

        <nav className="header__nav" hidden={!menuOpen && undefined} id="primary-nav"
          data-open={menuOpen || undefined}>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeMenu}
              className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}
            >
              <Icon name={item.icon} />
              {t(item.label)}
            </NavLink>
          ))}
        </nav>

        <div className="header__side">
          {company?.name && <span className="header__company" title={company.name}>{company.name}</span>}

          <NavLink to="/security" className="header__account" title={t('twofactor.myAccount')}>
            <Icon name="shield" size={14} />
            <span className="header__account-name">{user ? user.firstName : ''}</span>
          </NavLink>

          <div className="btn-group" role="group" aria-label={t('nav.language')}>
            {LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                className={`btn btn--sm${code === locale ? ' btn--primary' : ' btn--ghost'}`}
                onClick={() => void setUserLocale(code)}
                aria-pressed={code === locale}
                title={t('nav.language')}
              >
                {LOCALE_LABELS[code]}
              </button>
            ))}
          </div>

          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void signOut()}>
            <Icon name="log-out" />
            <span className="nowrap">{t('nav.signOut')}</span>
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--icon header__toggle"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="primary-nav"
            aria-label={t('nav.menu')}
          >
            <Icon name={menuOpen ? 'x' : 'menu'} size={18} />
          </button>
        </div>
      </header>

      {/* `key` resets page state (search terms, pagination) between routes. */}
      <main className="main" key={location.pathname.split('/')[1]}>
        <Outlet />
      </main>
    </div>
  );
}
